import { useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Upload, CheckCircle, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/photobooth`;

export default function UploadPage() {
  const { templateId } = useParams();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [userName, setUserName] = useState("");
  const [userContact, setUserContact] = useState("");

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setSuccess(false);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a photo first");
      return;
    }

    if (!userName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!userContact.trim()) {
      toast.error("Please enter your contact");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("user_name", userName);
    formData.append("user_contact", userContact);

    try {
      await axios.post(`${API}/upload/${templateId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      setSuccess(true);
      toast.success("Photo uploaded successfully! 🎉");
      
      setTimeout(() => {
        setFile(null);
        setPreview(null);
        setSuccess(false);
        setUserName("");
        setUserContact("");
      }, 3000);
    } catch (error) {
      if (error.response?.status === 507) {
        toast.error("Out of memory. Please try with a smaller image (under 5MB).");
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to upload photo. Please try with a smaller image.");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-0 shadow-2xl backdrop-blur-sm bg-white/95" data-testid="upload-card">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
            <Camera className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-600 to-purple-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Upload Your Photo
          </CardTitle>
          <CardDescription className="text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
            Enter your details and upload your photo
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!success ? (
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    data-testid="user-name-input"
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact (Phone/Email) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    placeholder="Phone number or email"
                    value={userContact}
                    onChange={(e) => setUserContact(e.target.value)}
                    data-testid="user-contact-input"
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="border-3 border-dashed border-purple-300 rounded-xl p-8 text-center bg-purple-50/50 hover:bg-purple-100/50 transition-colors">
                  <Input
                    id="photo-upload"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    data-testid="photo-upload-input"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                        <Upload className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700">Tap to take photo or choose from gallery</p>
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG - Keep under 5MB for best results</p>
                      </div>
                    </div>
                  </label>
                </div>

                {preview && (
                  <div className="mt-4" data-testid="photo-preview">
                    <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
                    <div className="rounded-xl overflow-hidden border-4 border-purple-200 shadow-lg">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-64 object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>

              <Button
                data-testid="submit-photo-btn"
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {uploading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                ) : (
                  "Upload Photo"
                )}
              </Button>
            </>
          ) : (
            <div className="text-center py-8" data-testid="success-message">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center mb-4 animate-bounce">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-600 mb-2">Success!</h3>
              <p className="text-gray-600">Your photo has been added to the template</p>
              <p className="text-sm text-gray-500 mt-4">You can upload another photo now</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}